import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Drawer, Form, Select, Button, Space, Checkbox, Image, Spin, message, Card, Row, Col, Modal, Tooltip, Upload } from 'antd';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useWarehouses } from '../hooks/useWarehouses';
import { usePdaMenuConfig } from '../hooks/usePdaMenuConfig';
import { useSavePdaMenuConfig } from '../hooks/useSavePdaMenuConfig';
import { GET_PDA_HOME_MENU_BASIC_CONFIG, UPDATE_PDA_MENU_CONFIG_ICON, UPLOAD_PDA_MENU_ICON } from '../graphql/queries';

const { Option } = Select;

const getAllRoleOptions = (t: any) => [
  { value: 'HUB_OPERATOR', label: t('pda.homeConfig.roles.HUB_OPERATOR'), siteType: 1 },
  { value: 'HUB_MANAGER', label: t('pda.homeConfig.roles.HUB_MANAGER'), siteType: 1 },
  { value: 'PICKUP_STAFF', label: t('pda.homeConfig.roles.PICKUP_DRIVER'), siteType: 1 },
  { value: 'SORTING_STAFF', label: t('pda.homeConfig.roles.SORTING_STAFF'), siteType: 1 },
  { value: 'RETURNS_STAFF', label: t('pda.homeConfig.roles.RETURNS_STAFF'), siteType: 1 },
  { value: 'SORTER_OPERATOR', label: t('pda.homeConfig.roles.SORTER_OPERATOR'), siteType: 1 },
  { value: 'WAREHOUSE_OPERATOR', label: t('pda.homeConfig.roles.WAREHOUSE_OPERATOR'), siteType: 2 },
  { value: 'WAREHOUSE_MANAGER', label: t('pda.homeConfig.roles.WAREHOUSE_MANAGER'), siteType: 2 },
];

interface MenuConfigItem {
  id: number;
  siteType?: string;
  configPath: string;
  configIcon: string;
  i18nKey: string;
  backgroundColor: string;
  isChecked?: boolean;
}

interface MenuConfigSection {
  id: number;
  siteType?: string;
  i18nKey: string;
  isChecked?: boolean;
  items: MenuConfigItem[];
}

interface PdaHomeConfigDrawerProps {
  visible: boolean;
  editingConfig: { warehouseId: number; role: string } | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * PDA 菜单项互斥组：同组内多个 `configPath` 只能同时保留其中一个 `preferred`。
 * 影响两处逻辑：
 *   1. `handleMenuItemToggle`（单点选）：勾选任一互斥项时，自动取消其它同组项
 *   2. `handleSectionToggle`（区域全选）：全选时把非 `preferred` 的同组项过滤掉，
 *      只保留 `preferred`，避免同一功能出现两个入口
 */
const MUTUALLY_EXCLUSIVE_GROUPS: ReadonlyArray<{ paths: readonly string[]; preferred: string }> = [
  // 装包扫描：`/pda/bagging`（旧单流向）与 `/pda/baggingScanPage`（新多流向），默认保留多流向
  { paths: ['/pda/bagging', '/pda/baggingScanPage'], preferred: '/pda/baggingScanPage' },
];

/**
 * 同 i18nKey 但不同 configPath 的菜单项，drawer 里需要在 `t(item.i18nKey)` 前拼前缀
 * 加以区分（例如两条"装包扫描"、两条"异常"）。
 * 前缀由 `pda.homeConfig.pathPrefix.*` 三语兜底，仅影响 drawer 展示，不影响 PDA 首页真实菜单。
 */
const PATH_LABEL_PREFIX_KEYS: Readonly<Record<string, string>> = {
  '/pda/baggingScanPage': 'pda.homeConfig.pathPrefix.multiFlow',
  '/pda/hubException': 'pda.homeConfig.pathPrefix.hub',
  '/pda/warehouseException': 'pda.homeConfig.pathPrefix.whs',
};

// PDA 菜单图标：后端返回的是 PNG 直链，直接用浏览器原生 <img> 渲染
// 兼容 PNG/JPG/SVG/WebP 等所有图片格式；走浏览器 HTTP cache；移除 dangerouslySetInnerHTML 的 XSS 风险
const SvgIcon: React.FC<{ url: string; alt: string }> = ({ url, alt }) => {
  const [error, setError] = React.useState(false);

  // url 变化（如替换图标后 refetch 拿到新 URL）时重置 error；
  // 否则一次加载失败后 error 永远为 true，新 url 也只能渲染成 `?`
  React.useEffect(() => {
    setError(false);
  }, [url]);

  // 加载失败时显示问号
  if (error || !url) {
    return <span style={{ color: 'white', fontSize: '20px' }}>?</span>;
  }

  return (
    <img
      src={url}
      alt={alt}
      onError={() => setError(true)}
      style={{
        width: '32px',
        height: '32px',
        objectFit: 'contain',
      }}
    />
  );
};

// 菜单图标编辑 Modal：
// 1. 用户选图 → File → base64 → 调 uploadPdaMenuIcon mutation（后端代上传 R2，绕开浏览器 CORS）
// 2. 拿到 objectKey 后本地用 ObjectURL 预览（不依赖 R2 GET，避免缓存一致性问题）
// 3. 确认 → 调 updatePdaMenuConfigIcon → mutation 配置了 refetchQueries + awaitRefetchQueries，
//    返回时 GetPdaHomeMenuBasicConfig 已自动 refetch，外层抽屉立即看到新图标
const MenuIconEditModal: React.FC<{
  visible: boolean;
  menuItemId: number | null;
  currentIconUrl: string;
  backgroundColor: string;
  onClose: () => void;
}> = ({ visible, menuItemId, currentIconUrl, backgroundColor, onClose }) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string>(''); // 本地 ObjectURL 预览（未确认前）
  const [uploadedObjectKey, setUploadedObjectKey] = useState<string>(''); // 上传到 R2 后的 objectKey
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewError, setPreviewError] = useState(false); // 预览图加载失败时显示 `?` fallback

  const [uploadPdaMenuIcon] = useMutation(UPLOAD_PDA_MENU_ICON);
  // 替换成功后自动 refetch GetPdaHomeMenuBasicConfig 并等其完成再返回，确保 mutation 返回时抽屉数据已最新
  const [updateConfigIcon] = useMutation(UPDATE_PDA_MENU_CONFIG_ICON, {
    refetchQueries: [{ query: GET_PDA_HOME_MENU_BASIC_CONFIG }],
    awaitRefetchQueries: true,
  });

  // 关闭时清理状态
  useEffect(() => {
    if (!visible) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      setUploadedObjectKey('');
      setUploading(false);
      setSubmitting(false);
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // 上传：file → base64 → 调后端代上传 mutation
  // 后端用 S3 SDK PutObject 到公开桶，避开浏览器 → R2 直传的 CORS 限制
  const handleUpload = useCallback(async (file: File) => {
    // 每次新上传开始前，先清掉上一次成功的 objectKey 和预览
    // 否则如果用户：上传 A 成功 → 再选 B 上传失败 → uploadedObjectKey 还是 A 的 → 确认时写库的是 A，与用户认知不符
    setUploadedObjectKey('');
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setUploading(true);
    try {
      // 1. File → base64（去掉 data:image/png;base64, 前缀）
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const commaIdx = result.indexOf(',');
          resolve(commaIdx === -1 ? result : result.substring(commaIdx + 1));
        };
        reader.onerror = () => reject(new Error('File read failed'));
        reader.readAsDataURL(file);
      });

      // 2. 调后端代上传 mutation
      const { data } = await uploadPdaMenuIcon({
        variables: {
          input: {
            fileBase64,
            filename: file.name,
            contentType: file.type,
          },
        },
      });
      const objectKey = data?.uploadPdaMenuIcon?.objectKey as string | undefined;
      if (!objectKey) {
        throw new Error('Upload failed: no objectKey returned');
      }

      // 3. 本地生成预览 ObjectURL，避免依赖 R2 GET CORS / cache 一致性
      // 用 functional setState 拿到最新值再 revoke，避免 useCallback 依赖 previewUrl 导致频繁重建
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return localUrl;
      });
      setUploadedObjectKey(objectKey);
      message.success(t('pda.homeConfig.iconUploaded'));
    } catch (err: any) {
      message.error(err?.message || t('pda.homeConfig.iconUploadFailed'));
    } finally {
      setUploading(false);
    }
    return false; // 阻止 antd Upload 默认行为
  }, [uploadPdaMenuIcon, t]);

  // 确认：调 mutation 替换 → 关闭
  const handleConfirm = useCallback(async () => {
    if (!menuItemId || !uploadedObjectKey) {
      message.warning(t('pda.homeConfig.pleaseUploadIcon'));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await updateConfigIcon({
        variables: { id: menuItemId, configIcon: uploadedObjectKey },
      });
      if (data?.updatePdaMenuConfigIcon) {
        message.success(t('pda.homeConfig.iconUpdated'));
        onClose();
      } else {
        message.error(t('pda.homeConfig.iconUpdateFailed'));
      }
    } catch (err: any) {
      message.error(err?.message || t('pda.homeConfig.iconUpdateFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [menuItemId, uploadedObjectKey, updateConfigIcon, t, onClose]);

  // 预览：上传后用本地 ObjectURL，否则显示原图标
  const displayUrl = previewUrl || currentIconUrl;

  // displayUrl 变化（上传成功 / 切到不同菜单项）时重置 previewError，避免一次失败永远显示 `?`
  useEffect(() => {
    setPreviewError(false);
  }, [displayUrl]);

  return (
    <Modal
      open={visible}
      title={t('pda.homeConfig.editIcon')}
      onCancel={onClose}
      onOk={handleConfirm}
      confirmLoading={submitting}
      okButtonProps={{ disabled: !uploadedObjectKey }}
      okText={t('common.confirm')}
      cancelText={t('common.cancel')}
      destroyOnClose
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div
          className="flex items-center justify-center"
          style={{
            backgroundColor: backgroundColor || '#3B82F6',
            borderRadius: 12,
            width: 96,
            height: 96,
            padding: 16,
          }}
        >
          {displayUrl && !previewError ? (
            <img
              src={displayUrl}
              alt="preview"
              onError={() => setPreviewError(true)}
              style={{ width: '64px', height: '64px', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ color: 'white', fontSize: 28 }}>?</span>
          )}
        </div>
        <Upload
          accept="image/png,image/jpeg,image/jpg,image/webp"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading || submitting}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {t('pda.homeConfig.uploadIcon')}
          </Button>
        </Upload>
      </div>
    </Modal>
  );
};

const PdaHomeConfigDrawer: React.FC<PdaHomeConfigDrawerProps> = ({
  visible,
  editingConfig,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);

  const ALL_ROLE_OPTIONS = getAllRoleOptions(t);
  const { warehouses: rawWarehouses, loading: warehousesLoading } = useWarehouses();
  
  // 添加 Common 虚拟仓库（id=0）到仓库列表开头，或替换已有的 id=0 仓库为 Common
  const warehouses = useMemo(() => {
    // 过滤掉原有的 id=0 的仓库（如果有的话）
    const filteredWarehouses = rawWarehouses.filter((wh: any) => wh.id !== 0 && Number(wh.id) !== 0);
    // 在开头添加 Common 虚拟仓库，siteType 设置为 "1,2" 表示同时支持 Hub 和 Warehouse
    const result = [{ id: 0, name: 'Common', siteType: '1,2', sortingCode: 'COMMON' }, ...filteredWarehouses];
    return result;
  }, [rawWarehouses]);
  
  const { menuConfig, existingMenuIds, loading: configLoading } = usePdaMenuConfig(
    selectedWarehouseId,
    selectedRole,
    !!editingConfig
  );
  const { save, loading: saving } = useSavePdaMenuConfig();

  // 图标编辑 Modal 状态（仅在 warehouseId=0 即 Common 仓库时启用）
  const [iconEditingItem, setIconEditingItem] = useState<MenuConfigItem | null>(null);
  const isCommonWarehouse = Number(selectedWarehouseId) === 0;

  useEffect(() => {
    if (visible && editingConfig) {
      // 确保 warehouseId 是数字类型，以便与 Select Option 的 value 匹配
      const warehouseId = typeof editingConfig.warehouseId === 'string' 
        ? Number(editingConfig.warehouseId) 
        : editingConfig.warehouseId;
      
      setSelectedWarehouseId(warehouseId);
      setSelectedRole(editingConfig.role);
      form.setFieldsValue({
        warehouseId: warehouseId,
        role: editingConfig.role,
      });
    } else if (visible && !editingConfig) {
      form.resetFields();
      setSelectedWarehouseId(null);
      setSelectedRole(null);
      setSelectedMenuIds([]);
    }
  }, [visible, editingConfig, form]);

  useEffect(() => {
    // 关闭时不动 selectedMenuIds（避免每次关闭都清空视觉状态）；
    // visible false→true 打开时强制根据 existingMenuIds 重置 selectedMenuIds，
    // 修复"重复打开同一 (warehouseId, role) 组合时 existingMenuIds 引用不变
    // 导致上次未保存的中间勾选态残留"的问题。
    if (!visible) return;
    if (existingMenuIds.length > 0) {
      setSelectedMenuIds(existingMenuIds);
    } else {
      setSelectedMenuIds([]);
    }
  }, [visible, existingMenuIds]);

  // 根据选中的仓库类型，动态计算可用的角色选项
  const availableRoles = useMemo(() => {
    if (!selectedWarehouseId) {
      return ALL_ROLE_OPTIONS;
    }
    
    // Common 仓库（id=0）可以选择所有角色
    if (Number(selectedWarehouseId) === 0) {
      return ALL_ROLE_OPTIONS;
    }
    
    const selectedWarehouse = warehouses.find((wh: any) => Number(wh.id) === Number(selectedWarehouseId));
    if (!selectedWarehouse) {
      return ALL_ROLE_OPTIONS;
    }
    
    // 根据仓库类型过滤角色
    // siteType=1 (Hub): Hub操作员、Hub管理员、揽收人员、分拣人员
    // siteType=2 (Warehouse): 仓库操作员、仓库管理员
    return ALL_ROLE_OPTIONS.filter(role => role.siteType === selectedWarehouse.siteType);
  }, [selectedWarehouseId, warehouses, ALL_ROLE_OPTIONS]);

  const filteredMenuConfig = useMemo(() => {
    if (!menuConfig || !selectedRole) return [];
    
    const isCommonWarehouse = Number(selectedWarehouseId) === 0;
    
    // Common 仓库不做任何过滤，显示所有数据（1、2、1,2）
    if (isCommonWarehouse) {
      return menuConfig;
    }
    
    // 普通仓库：根据仓库的 siteType 过滤
    const selectedWarehouse = warehouses.find((wh: any) => Number(wh.id) === Number(selectedWarehouseId));
    const currentSiteType = selectedWarehouse?.siteType; // 1-HUB, 2-WAREHOUSE

    // 检查 siteType 是否匹配的辅助函数
    const isSiteTypeMatch = (siteTypeStr: string | undefined): boolean => {
      // 如果没有配置 siteType，则默认显示
      if (!siteTypeStr) {
        return true;
      }
      
      // 检查 siteType 是否包含当前仓库的类型
      const allowedSiteTypes = siteTypeStr.split(',').map(t => parseInt(t.trim()));
      return allowedSiteTypes.includes(currentSiteType);
    };

    return menuConfig
      .filter((section: MenuConfigSection) => {
        // 首先过滤父区域的 siteType
        return isSiteTypeMatch(section.siteType);
      })
      .map((section: MenuConfigSection) => {
        // 再过滤该区域下的菜单项
        const filteredItems = section.items.filter((item: MenuConfigItem) => {
          return isSiteTypeMatch(item.siteType);
        });
        
        return {
          ...section,
          items: filteredItems
        };
      })
      .filter((section: MenuConfigSection) => section.items.length > 0); // 过滤掉没有菜单项的区域
  }, [menuConfig, selectedRole, selectedWarehouseId, warehouses]);

  const handleRoleChange = useCallback((role: string) => {
    setSelectedRole(role);
  }, []);

  const handleWarehouseChange = useCallback((warehouseId: number) => {
    setSelectedWarehouseId(warehouseId);
    
    // 计算新仓库的可用角色列表
    let newAvailableRoles = ALL_ROLE_OPTIONS;
    
    // Common 仓库（id=0）可以选择所有角色
    if (Number(warehouseId) !== 0) {
      const selectedWarehouse = warehouses.find((wh: any) => Number(wh.id) === Number(warehouseId));
      if (selectedWarehouse) {
        // 根据仓库类型过滤角色
        newAvailableRoles = ALL_ROLE_OPTIONS.filter(role => role.siteType === selectedWarehouse.siteType);
      }
    }
    
    // 自动选中第一个可用角色
    if (newAvailableRoles.length > 0) {
      const firstRole = newAvailableRoles[0].value;
      setSelectedRole(firstRole);
      form.setFieldValue('role', firstRole);
    } else {
      setSelectedRole(null);
      form.setFieldValue('role', undefined);
    }
  }, [warehouses, form, ALL_ROLE_OPTIONS]);

  const handleMenuItemToggle = useCallback((menuId: number) => {
    setSelectedMenuIds(prev => {
      const newIds = [...prev];
      
      if (prev.includes(menuId)) {
        // 取消选中按钮
        const index = newIds.indexOf(menuId);
        newIds.splice(index, 1);
        
        // 找到该按钮所属的区域
        const section = filteredMenuConfig.find((s: MenuConfigSection) => 
          s.items.some((item: MenuConfigItem) => item.id === menuId)
        );
        if (section) {
          const sectionItemIds = section.items.map((item: MenuConfigItem) => item.id);
          // 检查该区域是否还有其他按钮被选中
          const hasOtherItemsSelected = sectionItemIds.some((id: number) => id !== menuId && newIds.includes(id));
          // 如果该区域没有其他按钮被选中了，移除区域 ID
          if (!hasOtherItemsSelected) {
            const sectionIndex = newIds.indexOf(section.id);
            if (sectionIndex !== -1) {
              newIds.splice(sectionIndex, 1);
            }
          }
        }
      } else {
        // 选中按钮前，先处理互斥逻辑
        const currentItem = filteredMenuConfig
          .flatMap((s: MenuConfigSection) => s.items)
          .find((item: MenuConfigItem) => item.id === menuId);
        
        if (currentItem) {
          const currentPath = currentItem.configPath;

          // 检查当前路径是否在互斥组中（`MUTUALLY_EXCLUSIVE_GROUPS` 模块级常量与 `handleSectionToggle` 共用）
          for (const group of MUTUALLY_EXCLUSIVE_GROUPS) {
            if (group.paths.includes(currentPath)) {
              // 找到互斥的其他路径
              const excludePaths = group.paths.filter(p => p !== currentPath);

              // 找到所有互斥路径对应的菜单项并取消选中
              const mutuallyExclusiveItems = filteredMenuConfig
                .flatMap((s: MenuConfigSection) => s.items)
                .filter((item: MenuConfigItem) => excludePaths.includes(item.configPath));

              mutuallyExclusiveItems.forEach((item: MenuConfigItem) => {
                const idx = newIds.indexOf(item.id);
                if (idx !== -1) {
                  newIds.splice(idx, 1);
                }
              });

              break;
            }
          }
        }
        
        // 选中按钮
        newIds.push(menuId);
        
        // 找到该按钮所属的区域，并确保区域 ID 也被添加
        const section = filteredMenuConfig.find((s: MenuConfigSection) => 
          s.items.some((item: MenuConfigItem) => item.id === menuId)
        );
        if (section && !newIds.includes(section.id)) {
          newIds.push(section.id);
        }
      }
      
      return newIds;
    });
  }, [filteredMenuConfig]);

  // 计算只包含按钮的选中数量（排除区域 ID）
  const selectedButtonCount = useMemo(() => {
    // 收集所有区域的 ID
    const sectionIds = filteredMenuConfig.map((section: MenuConfigSection) => section.id);
    // 过滤掉区域 ID，只保留按钮 ID
    return selectedMenuIds.filter(id => !sectionIds.includes(id)).length;
  }, [selectedMenuIds, filteredMenuConfig]);

  const handleSectionToggle = useCallback((section: MenuConfigSection) => {
    const sectionItemIds = section.items.map((item: MenuConfigItem) => item.id);
    const sectionId = section.id; // 区域本身的 ID
    const allIds = [sectionId, ...sectionItemIds]; // 包含区域 ID 和所有按钮 ID
    
    const allSelected = allIds.every((id: number) => selectedMenuIds.includes(id));
    
    if (allSelected) {
      // 取消选中：移除区域 ID 和所有按钮 ID
      setSelectedMenuIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      // 选中：添加区域 ID 和所有按钮 ID，然后按 `MUTUALLY_EXCLUSIVE_GROUPS` 过滤——
      // 互斥组只保留 preferred，避免同一功能被区域全选顺带选进两次
      setSelectedMenuIds(prev => {
        const newIds = [...prev];
        allIds.forEach((id: number) => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });

        // 收集本区域里所有互斥组中"非 preferred"的 item id，从 newIds 中剔除
        const nonPreferredItemIds = section.items
          .filter((item: MenuConfigItem) => MUTUALLY_EXCLUSIVE_GROUPS.some(
            group => group.paths.includes(item.configPath) && item.configPath !== group.preferred,
          ))
          .map((item: MenuConfigItem) => item.id);

        return nonPreferredItemIds.length === 0
          ? newIds
          : newIds.filter(id => !nonPreferredItemIds.includes(id));
      });
    }
  }, [selectedMenuIds]);

  const handleSave = useCallback(async () => {
    if (!selectedWarehouseId || !selectedRole) {
      message.error(t('pda.homeConfig.selectWarehouse') + ' ' + t('pda.homeConfig.selectRole'));
      return;
    }

    // 二次确认
    Modal.confirm({
      title: editingConfig ? t('pda.homeConfig.confirmEdit') : t('pda.homeConfig.confirmSave'),
      content: editingConfig 
        ? t('pda.homeConfig.confirmEditMessage')
        : t('pda.homeConfig.confirmSaveMessage'),
      okText: t('pda.homeConfig.save'),
      cancelText: t('pda.homeConfig.cancel'),
      onOk: async () => {
        // 对 menuConfigIds 进行排序（从小到大）
        const sortedMenuConfigIds = [...selectedMenuIds].sort((a, b) => a - b);

        const result = await save({
          warehouseId: selectedWarehouseId,
          accountRole: selectedRole,
          menuConfigIds: sortedMenuConfigIds,
          isEdit: !!editingConfig,
        });

        if (result.success) {
          message.success(t('pda.homeConfig.saveSuccess'));
          onSuccess();
        } else {
          message.error(result.error || t('pda.homeConfig.saveFailed'));
        }
      },
    });
  }, [selectedWarehouseId, selectedRole, selectedMenuIds, save, onSuccess, editingConfig, t]);

  // 同 i18nKey 不同 configPath 的菜单项在 drawer 里需要前缀区分；
  // 未命中映射时直接返回原 i18nKey 文案，保持不变。
  const resolveMenuLabel = (item: MenuConfigItem): string => {
    const prefixKey = PATH_LABEL_PREFIX_KEYS[item.configPath];
    const base = t(item.i18nKey);
    return prefixKey ? `${t(prefixKey)}${base}` : base;
  };

  const renderMenuItem = (item: MenuConfigItem) => {
    const isChecked = selectedMenuIds.includes(item.id);
    const label = resolveMenuLabel(item);

    return (
      <Tooltip title={item.configPath} placement="top">
        <Card 
          size="small" 
          className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => handleMenuItemToggle(item.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Checkbox checked={isChecked} />
              {(() => {
                // 仅 Common 仓库（warehouseId=0）下可点击图标进入编辑
                const iconClickable = isCommonWarehouse;
                const handleIconClick = (e: React.MouseEvent) => {
                  if (!iconClickable) return;
                  // 阻止冒泡，避免触发外层 Card 的 toggle
                  e.stopPropagation();
                  setIconEditingItem(item);
                };
                const iconHoverStyle = iconClickable
                  ? { cursor: 'pointer' as const }
                  : undefined;
                return item.configIcon ? (
                  <Tooltip title={iconClickable ? t('pda.homeConfig.editIcon') : undefined}>
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: item.backgroundColor || '#3B82F6',
                        borderRadius: 8,
                        width: 48,
                        height: 48,
                        padding: 8,
                        overflow: 'hidden',
                        ...iconHoverStyle,
                      }}
                      onClick={handleIconClick}
                    >
                      <SvgIcon url={item.configIcon} alt={label} />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip title={iconClickable ? t('pda.homeConfig.editIcon') : undefined}>
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: '#E5E7EB',
                        borderRadius: 8,
                        width: 48,
                        height: 48,
                        ...iconHoverStyle,
                      }}
                      onClick={handleIconClick}
                    >
                      <span style={{ fontSize: 24, color: '#9CA3AF' }}>?</span>
                    </div>
                  </Tooltip>
                );
              })()}
              <span className="flex-1">{label}</span>
            </div>
          </div>
        </Card>
      </Tooltip>
    );
  };

  const renderSection = (section: MenuConfigSection) => {
    const sectionItemIds = section.items.map((item: MenuConfigItem) => item.id);
    const sectionId = section.id;
    const allIds = [sectionId, ...sectionItemIds]; // 包含区域 ID 和所有按钮 ID
    
    // 检查区域 ID 和所有按钮 ID 是否都被选中
    const allChecked = allIds.every((id: number) => selectedMenuIds.includes(id)) && allIds.length > 0;
    // 检查是否有部分选中（至少有一个按钮被选中，但不是全部）
    const itemsCheckedCount = sectionItemIds.filter((id: number) => selectedMenuIds.includes(id)).length;
    const indeterminate = itemsCheckedCount > 0 && !allChecked;

    return (
      <div key={section.id} className="mb-6">
        <div 
          className="flex items-center mb-3 p-2 bg-gray-50 rounded cursor-pointer"
          onClick={() => handleSectionToggle(section)}
        >
          <Checkbox 
            checked={allChecked} 
            indeterminate={indeterminate}
          />
          <span className="ml-2 font-medium text-base">{t(section.i18nKey)}</span>
          <span className="ml-auto text-gray-500 text-sm">
            {itemsCheckedCount}/{sectionItemIds.length} {t('pda.homeConfig.selected')}
          </span>
        </div>
        <div className="pl-6">
          <Row gutter={[16, 16]}>
            {section.items.map((item: MenuConfigItem) => (
              <Col key={item.id} span={8}>
                {renderMenuItem(item)}
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={
        <div className="flex items-center justify-start w-full flex-wrap">
          <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">
            {editingConfig ? t('pda.homeConfig.editConfig') : t('pda.homeConfig.newConfig')}
          </span>
        </div>
      }
      width={800}
      visible={visible}
      onClose={onClose}
      closable={false}
      extra={
        <Button
          type="text"
          onClick={onClose}
          icon={<CloseOutlined />}
        />
      }
      footer={
        <Space className="flex justify-end">
          <Button onClick={onClose}>{t('pda.homeConfig.cancel')}</Button>
          <Button 
            type="primary" 
            onClick={handleSave} 
            loading={saving}
            disabled={!selectedWarehouseId || !selectedRole || selectedMenuIds.length === 0}
          >
            {t('pda.homeConfig.save')}
          </Button>
        </Space>
      }
    >
      <Spin spinning={configLoading}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="warehouseId"
            label={t('pda.homeConfig.warehouse')}
            rules={[{ required: true, message: String(t('pda.homeConfig.selectWarehouse')) }]}
          >
            <Select
              placeholder={t('pda.homeConfig.selectWarehouse')}
              loading={warehousesLoading}
              onChange={handleWarehouseChange}
              disabled={!!editingConfig}
            >
              {warehouses.map((wh: any) => (
                <Option key={wh.id} value={Number(wh.id)}>
                  {wh.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="role"
            label={t('pda.homeConfig.role')}
            rules={[{ required: true, message: String(t('pda.homeConfig.selectRole')) }]}
          >
            <Select
              placeholder={t('pda.homeConfig.selectRole')}
              onChange={handleRoleChange}
              disabled={!!editingConfig}
            >
              {availableRoles.map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {selectedRole && (
          <div className="mt-6">
            <div className="mb-4 p-3 bg-blue-50 rounded">
              <div className="text-sm text-gray-600">
                {t('pda.homeConfig.selectedItems', { count: selectedButtonCount })}
              </div>
            </div>
            
            {filteredMenuConfig.length > 0 ? (
              filteredMenuConfig.map((section: MenuConfigSection) => renderSection(section))
            ) : (
              <div className="text-center text-gray-400 py-8">
                {t('common.noData')}
              </div>
            )}
          </div>
        )}
      </Spin>
      {/* Common 仓库下，点击单个菜单图标弹出编辑 Modal */}
      {/* Modal 内部 useMutation 已配置 refetchQueries + awaitRefetchQueries 自动刷新 GetPdaHomeMenuBasicConfig，外层不用再手动 refetch */}
      <MenuIconEditModal
        visible={!!iconEditingItem}
        menuItemId={iconEditingItem?.id ?? null}
        currentIconUrl={iconEditingItem?.configIcon ?? ''}
        backgroundColor={iconEditingItem?.backgroundColor ?? '#3B82F6'}
        onClose={() => setIconEditingItem(null)}
      />
    </Drawer>
  );
};

export default PdaHomeConfigDrawer;
